import React, { useState, useEffect } from "react";
import {
  Container,
  Card,
  Form,
  Row,
  Col,
  Table,
  Button
} from "react-bootstrap";
import { Download } from "lucide-react";
import { format } from "date-fns";
import TooltipOption from "../../../core/common/tooltipOption";
import { Link } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import { _get } from "../../Utils/Helper";
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import "jspdf-autotable";

const ExpensesReport = () => {
  const routes = all_routes;

  const [filterType, setFilterType] = useState<"daily" | "monthly" | "session">(
    "daily"
  );
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedSession, setSelectedSession] = useState("");

  const [expensesTransactions, setExpensesTransactions] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    setLoading(true);
    _get(
      "expenses-report",
      (data: any[]) => {
        setExpensesTransactions(Array.isArray(data) ? data : [data]);
        setLoading(false);
      },
      (err) => {
        console.error("Failed to fetch expenses transactions:", err);
        setLoading(false);
      }
    );
  }, []);

  const filteredTransactions = Array.isArray(expensesTransactions)
    ? expensesTransactions.filter((transaction) => {
        if (filterType === "daily" && selectedDate) {
          return transaction.date === selectedDate;
        }
        if (filterType === "monthly" && selectedMonth) {
          return transaction.date.startsWith(selectedMonth);
        }
        if (filterType === "session" && selectedSession) {
          return true;
        }
        return true;
      })
    : [];

  const totalExpenses = filteredTransactions.reduce((sum, t) => {
    const amount = parseFloat(t.total_expenses);
    return isNaN(amount) ? sum : sum + amount;
  }, 0);

  const handleDownloadExcel = () => {
    const data = filteredTransactions.map((t) => ({
      Date: format(new Date(t.date), "yyyy-MM-dd"),
      Category: t.category,
      Description: t.description,
      Amount: parseFloat(t.total_expenses).toLocaleString(),
      "Payment Method": t.payment_method
    }));

    const summary = [
      { Label: "Total Expenses", Amount: totalExpenses.toLocaleString() }
    ];

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(data);
    const summaryWs = XLSX.utils.json_to_sheet(summary);

    XLSX.utils.book_append_sheet(wb, ws, "Expenses");
    XLSX.utils.book_append_sheet(wb, summaryWs, "Summary");

    XLSX.writeFile(wb, "expenses-report.xlsx");
  };

  const handleDownloadPDF = () => {
    const doc = new jsPDF();

    doc.text("Expenses Report", 14, 15);
    doc.text(
      `Filter Type: ${
        filterType === "daily"
          ? selectedDate
          : filterType === "monthly"
          ? selectedMonth
          : selectedSession
      }`,
      14,
      25
    );

    (doc as any).autoTable({
      head: [["Date", "Category", "Description", "Amount", "Payment Method"]],
      body: filteredTransactions.map((t) => [
        format(new Date(t.date), "yyyy-MM-dd"),
        t.category,
        t.description,
        parseFloat(t.total_expenses).toLocaleString(),
        t.payment_method
      ]),
      startY: 35
    });

    const finalY = (doc as any).lastAutoTable.finalY + 10;

    doc.text(`Total Expenses: ${totalExpenses.toLocaleString()}`, 14, finalY);

    doc.save("expenses-report.pdf");
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">Expenses Report</h3>
            {/* <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.adminDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="#">Report</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Expenses Report
                </li>
              </ol>
            </nav> */}
          </div>
          <TooltipOption />
        </div>
        <Container className="py-4">
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h2 className="mb-0">Expenses Report</h2>
              <div>
                <Button
                  variant="outline-primary"
                  className="me-2"
                  onClick={handleDownloadExcel}
                >
                  <Download size={16} className="me-2" />
                  Download Excel
                </Button>
                <Button variant="outline-primary" onClick={handleDownloadPDF}>
                  <Download size={16} className="me-2" />
                  Download PDF
                </Button>
              </div>
            </Card.Header>
            <Card.Body>
              <Row className="mb-4">
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Filter Type</Form.Label>
                    <Form.Select
                      value={filterType}
                      onChange={(e) =>
                        setFilterType(
                          e.target.value as "daily" | "monthly" | "session"
                        )
                      }
                    >
                      <option value="daily">Daily</option>
                      <option value="monthly">Monthly</option>
                      <option value="session">Session</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  {filterType === "daily" && (
                    <Form.Group>
                      <Form.Label>Select Date</Form.Label>
                      <Form.Control
                        type="date"
                        value={selectedDate}
                        onChange={(e) => setSelectedDate(e.target.value)}
                      />
                    </Form.Group>
                  )}
                  {filterType === "monthly" && (
                    <Form.Group>
                      <Form.Label>Select Month</Form.Label>
                      <Form.Control
                        type="month"
                        value={selectedMonth}
                        onChange={(e) => setSelectedMonth(e.target.value)}
                      />
                    </Form.Group>
                  )}
                  {filterType === "session" && (
                    <Form.Group>
                      <Form.Label>Select Session</Form.Label>
                      <Form.Select
                        value={selectedSession}
                        onChange={(e) => setSelectedSession(e.target.value)}
                      >
                        <option value="">Select Session</option>
                        <option value="2023/2024">2023/2024</option>
                        <option value="2024/2025">2024/2025</option>
                      </Form.Select>
                    </Form.Group>
                  )}
                </Col>
              </Row>
              <Table striped bordered hover responsive>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Category</th>
                    <th>Description</th>
                    <th>Amount</th>
                    <th>Payment Method</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={5} className="text-center">
                        Loading...
                      </td>
                    </tr>
                  ) : (
                    filteredTransactions.map((transaction, index) => {
                      const amount = parseFloat(transaction.total_expenses);
                      return (
                        <tr key={index}>
                          <td>
                            {format(new Date(transaction.date), "yyyy-MM-dd")}
                          </td>
                          <td>{transaction.category}</td>
                          <td>{transaction.description}</td>
                          <td className="text-danger">
                            {isNaN(amount)
                              ? "Invalid Amount"
                              : amount.toLocaleString()}
                          </td>
                          <td>{transaction.payment_method}</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </Table>
              <tr className="fw-bold">
                <td colSpan={3}>Total Expenses:</td>
                <td className="text-danger" colSpan={2}>
                  {totalExpenses.toLocaleString()}
                </td>
              </tr>
            </Card.Body>
          </Card>
        </Container>
      </div>
    </div>
  );
};

export default ExpensesReport;
